import type { MouseEvent, ReactNode } from "react";
import React, {
  Children,
  cloneElement,
  Component,
  type KeyboardEvent,
  useEffect,
} from "react";
import type { ConnectedProps } from "react-redux";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import classNames from "classnames";
import _ from "underscore";

import type { IconButtonProps } from "@braintrust/braintrust-ui-components";
import { styled } from "@braintrust/braintrust-ui-components";
import {
  IconButton,
  Modal as BUCModal,
  Paper,
} from "@braintrust/braintrust-ui-components";
import { CloseIcon } from "@braintrust/braintrust-ui-components/Icons";
import type { RootState } from "@js/store";
import { mapTypedDispatchToProps } from "@js/utils/store";

import { CancelledByUser } from "./cancel-by-user";
import type { ModalConfirmProps } from "./confirm";
import { ModalConfirm } from "./confirm";
import { closeModalAction, openModalAction } from "./slice";

import styles from "./style.module.scss";

/**
 * Example usage:
 *
 * ```
 * const TestModal = Modal('test');
 *
 * <TestModal>
 *     Hello, I'm a test modal!
 * </TestModal>
 * <a onClick={() => TestModal.open()}>
 *     Open Test Modal
 * </a>
 * ```
 */

const StyledPaper = styled(Paper)(() => ({
  borderRadius: 12,
  overflow: "hidden",
  boxShadow: `0px 1px 2px rgba(0, 0, 0, 0.04),
                 0px 20px 100px rgba(0, 0, 0, 0.12),
                 0px 8px 16px rgba(0, 0, 0, 0.04)`,
}));

export type ModalInstanceProps = {
  isModalOpen?: boolean;
  children?: React.ReactNode;
  closeButton?: boolean;
  closeButtonProps?: Partial<IconButtonProps>;
  padding?: boolean;
  containerScrollableNoMobilePadding?: boolean;
  keepOnBackdropClick?: boolean;
  disableEnforceFocus?: boolean;
  backdropInvisible?: boolean;
  disablePortal?: boolean;
  disableEscapeKeyDown?: boolean;
  className?: string;
  onClose?: () => void;
  onOpen?: () => void;
  onKeyDown?: (ev: KeyboardEvent<HTMLElement>) => void;
};

const initialInstanceProps: Partial<ModalInstanceProps> = {
  closeButton: true,
  padding: true,
  containerScrollableNoMobilePadding: false,
  keepOnBackdropClick: false,
  disableEnforceFocus: false,
  backdropInvisible: false,
  disablePortal: false,
  disableEscapeKeyDown: false,
} as const;

type ManagedModalProps = ConnectedProps<ReturnType<typeof connector>> &
  ModalInstanceProps;
export const Modal = (id: string, customProps?: ModalInstanceProps) => {
  let instance: null | ManagedModal = null;
  let instanceProps = {
    ...initialInstanceProps,
    ...customProps,
  };

  class ManagedModal extends Component<ManagedModalProps> {
    id: string;

    mouseDownTarget: React.MutableRefObject<unknown>;

    constructor(props: ManagedModalProps) {
      super(props);
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      instance = this;
      this.id = id;

      this.mouseDownTarget = React.createRef();
    }

    componentDidUpdate(prevProps: ManagedModalProps) {
      if (prevProps.isModalOpen && !this.props.isModalOpen) {
        if (instanceProps && instanceProps.onClose) {
          instanceProps.onClose();
          return;
        }

        if (this.props?.onClose) {
          this.props.onClose();
          return;
        }
      }

      if (!prevProps.isModalOpen && this.props.isModalOpen) {
        if (instanceProps && instanceProps.onOpen) {
          instanceProps.onOpen();
          return;
        }

        if (this.props.onOpen) {
          this.props.onOpen();
          return;
        }
      }
    }

    static close() {
      if (instance) {
        instance.props.dispatch(closeModalAction(instance.id));
      }
    }

    static open(event: React.MouseEvent): void;
    static open(props?: Omit<ModalInstanceProps, "isModalOpen">): void;

    static open(
      props: Omit<ModalInstanceProps, "isModalOpen"> | React.MouseEvent = {},
    ) {
      instanceProps = { ...initialInstanceProps, ...customProps, ...props };

      if (instance) {
        instance.props.dispatch(openModalAction(instance.id));
      }
    }

    get closeButton() {
      // let <ModalInstance closeButton={false} /> override instance props
      if (
        (this.props.closeButton === undefined && instanceProps.closeButton) ||
        this.props.closeButton
      ) {
        return (
          <IconButton
            size="small"
            variant="transparent"
            {...instanceProps.closeButtonProps}
            className={classNames(
              styles.modalCloseButton,
              instanceProps.closeButtonProps?.className,
            )}
            aria-label="Close modal"
            onClick={ManagedModal.close}
          >
            <CloseIcon sx={{ m: "0.5rem" }} />
          </IconButton>
        );
      }

      return null;
    }

    handleMouseDown = (event: MouseEvent) => {
      this.mouseDownTarget.current = event.target;
    };

    handleBackdropClick = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      if (
        event.target !== event.currentTarget ||
        event.target !== this.mouseDownTarget.current ||
        event.target.clientWidth < event.pageX
      ) {
        return;
      }

      this.mouseDownTarget.current = null;

      if (!instanceProps.keepOnBackdropClick) {
        ManagedModal.close();
      }
    };

    render() {
      const contentClassName = classNames(
        styles.modalContent,
        instanceProps.className || undefined,
        { [styles.withPadding]: instanceProps.padding },
      );

      const children = instanceProps.children || this.props.children;

      return (
        <BUCModal
          className="modal-content-container"
          open={this.props.isModalOpen ?? false}
          onClose={(_event, reason) => {
            if (
              instanceProps.keepOnBackdropClick &&
              reason === "backdropClick"
            ) {
              return;
            }

            if (
              reason === "escapeKeyDown" &&
              instanceProps.disableEscapeKeyDown
            ) {
              return;
            }

            ManagedModal.close();
          }}
          disableEnforceFocus={instanceProps.disableEnforceFocus}
          disablePortal={instanceProps.disablePortal}
          slotProps={{
            backdrop: instanceProps.backdropInvisible
              ? {
                  invisible: true,
                }
              : {
                  className: styles.modalBackdropBlack,
                },
          }}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div
            data-testid="modal-container"
            className={classNames(styles.modalContentContainerScrollable, {
              [styles.modalContentNoPadding]:
                instanceProps.containerScrollableNoMobilePadding,
            })}
            onKeyDown={
              instanceProps.onKeyDown ? instanceProps.onKeyDown : undefined
            }
            onMouseDown={this.handleMouseDown}
            onMouseUp={this.handleBackdropClick}
            role="dialog"
            aria-modal={true}
          >
            <StyledPaper className={contentClassName}>
              {this.closeButton}
              {Children.map(children, (child) => {
                if (!React.isValidElement(child)) return;

                return cloneElement(child, {
                  ...(typeof child.props === "object" ? child.props : {}),
                });
              })}
            </StyledPaper>
          </div>
        </BUCModal>
      );
    }
  }

  return connector(id)(ManagedModal);
};

const mapStateToProps = (state: RootState, id: string | number) => ({
  isModalOpen: state.modal[id] || false,
});

const connector = (id: string | number) =>
  connect(
    (state: RootState) => mapStateToProps(state, id),
    mapTypedDispatchToProps,
  );

type AsyncModalModalConfirmProps = Pick<
  ModalConfirmProps,
  "confirmText" | "cancelText"
>;

const openAsyncModal = (
  config: ModalInstanceProps,
  modalConfirmProps: AsyncModalModalConfirmProps,
  modalInstance = ModalInstance,
) => {
  return new Promise<void>((resolve, reject) => {
    modalInstance.open({
      ...config,
      children: (
        <ModalConfirm
          {...modalConfirmProps}
          onConfirm={() => {
            resolve();
          }}
          onCancel={() => {
            reject(new CancelledByUser());
          }}
        >
          {config.children}
        </ModalConfirm>
      ),
      onClose: () => {
        reject(new CancelledByUser());
      },
    });
  });
};

export const openModalAndWaitForInput = async (
  config: ModalInstanceProps,
  modalConfirmProps: AsyncModalModalConfirmProps,
) => {
  try {
    await openAsyncModal(config, modalConfirmProps, ModalInstance);
  } catch (error) {
    // rethrow
    const _err = error;
    throw _err;
  } finally {
    ModalInstance.close();
  }
};

type CloseModalOnLocationChangeProps = {
  children: ReactNode;
  closeModal: () => void;
};

export const CloseModalOnLocationChange = ({
  children,
  closeModal,
}: CloseModalOnLocationChangeProps) => {
  const location = useLocation();

  useEffect(() => {
    if (closeModal) {
      return () => {
        closeModal();
      };
    }
  }, [location, closeModal]);

  return <>{children}</>;
};

export const ModalInstance = Modal("modal-instance-1", {
  closeButton: false,
});
export const CommonConfirmationModal = Modal("common-confirmation-modal", {
  closeButton: false,
});
export * from "./confirm";
export * from "./prompt";
